<template>
  <div class="mapped-purchase pb-16">
    <v-row>
      <v-col cols="12" md="6" class="d-flex justify-center flex-column align-stretch">
        <t-btn
          v-if="mobile && mapUrl !== null"
          block
          variant="outlined"
          color="primary"
          prepend-icon="mdi-map-outline"
          class="mb-3"
          @click="mapOpen = true"
        >
          Ver mapa completo
        </t-btn>
        <sector-listing
          :groups="groups"
          :colors="colors"
          :active-id="opened?.id ?? null"
          @open="open"
        />
        <!-- The rows take their colours from the map's shapes, which a phone only shows on demand -->
        <svg-map
          v-if="mobile && mapUrl !== null"
          v-show="false"
          :source="mapUrl"
          @setup="paint"
        />
      </v-col>
      <v-col v-if="!mobile" md="6">
        <div class="side bg-white rounded pa-4">
          <section-seats
            v-if="opened !== null"
            :panel="opened"
            :description="describe(opened.sector)"
            :rows="rows"
            :loading="loadingRows"
            :selected="seatsInCart"
            :pending="ticketCart.pending"
            @toggle="tap"
            @close="close"
          />
          <svg-map
            v-else-if="mapUrl !== null"
            ref="venueMap"
            :source="mapUrl"
            class="d-block mx-auto"
            style="width: 60%"
            @click="openShape"
            @setup="paint"
          />
          <div v-else class="text-medium-emphasis pa-4">
            Escolha um setor para ver os lugares.
          </div>
        </div>
      </v-col>
    </v-row>

    <template v-if="mobile">
      <v-dialog :model-value="mapOpen" fullscreen @update:model-value="mapOpen = false">
        <div class="full-screen bg-white d-flex flex-column">
          <div class="d-flex align-center pa-2">
            <span class="flex-grow-1 font-weight-bold px-2">Mapa do evento</span>
            <t-btn
              icon="mdi-close"
              variant="text"
              aria-label="Fechar"
              @click="mapOpen = false"
            />
          </div>
          <div class="flex-grow-1 pa-3">
            <svg-map
              v-if="mapUrl !== null"
              ref="venueMap"
              :source="mapUrl"
              style="width: 100%"
              @click="openShape"
              @setup="paint"
            />
          </div>
          <cart-bar
            docked
            :event="event"
            :groups="groups"
            :hold-left="holdLeft"
            :busy="advancing"
            @advance="advance"
            @remove="release"
          />
        </div>
      </v-dialog>

      <v-dialog :model-value="opened !== null" fullscreen @update:model-value="close">
        <div v-if="opened !== null" class="full-screen bg-white d-flex flex-column">
          <section-seats
            full-screen
            class="flex-grow-1 pa-3"
            :panel="opened"
            :description="describe(opened.sector)"
            :rows="rows"
            :loading="loadingRows"
            :selected="seatsInCart"
            :pending="ticketCart.pending"
            @toggle="tap"
            @close="close"
          />
          <cart-bar
            docked
            :event="event"
            :groups="groups"
            :hold-left="holdLeft"
            :busy="advancing"
            @advance="advance"
            @remove="release"
          />
        </div>
      </v-dialog>
    </template>

    <v-bottom-sheet :model-value="picking !== null" @update:model-value="picking = null">
      <div v-if="picking !== null" class="type-sheet bg-white pa-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <span>Seção {{ picking.panel.section.name }} · {{ seatLabel(picking.seat) }}</span>
          <t-btn
            icon="mdi-close"
            variant="text"
            aria-label="Fechar"
            @click="picking = null"
          />
        </div>
        <button
          v-for="type in picking.panel.section.tickets"
          :key="type.id"
          class="type-card d-flex align-center ga-3 pa-3 mb-2 w-100"
          :data-type="type.id"
          :disabled="type.current_batch == null"
          @click="pick(type)"
        >
          <span class="flex-grow-1">
            <span class="font-weight-bold d-block">{{ type.name }}</span>
            <span v-if="type.display_batch && showBatch" class="type-batch text-caption text-medium-emphasis">
              Lote {{ type.display_batch.number }}
            </span>
          </span>
          <span v-if="type.display_batch" class="text-right">
            <span class="d-block">{{ formatCurrency(type.display_batch.price) }}</span>
            <span class="type-fee d-block text-caption text-medium-emphasis">
              + {{ formatCurrency(fee(type.display_batch.price)) }} de taxa
            </span>
          </span>
          <template v-if="type.current_batch">
            <!-- Only a cue: the whole card picks the type, and a button can't hold another one -->
            <t-btn
              v-if="mobile"
              tag="span"
              icon="mdi-plus"
              color="success"
              size="small"
              aria-hidden="true"
            />
            <t-btn
              v-else
              tag="span"
              color="success"
              size="small"
            >
              Adicionar
            </t-btn>
          </template>
          <span v-else>Esgotado</span>
        </button>
      </div>
    </v-bottom-sheet>

    <v-bottom-sheet :model-value="generalSheet !== null" @update:model-value="generalSheet = null">
      <div v-if="generalSheet !== null" class="general-sheet bg-white pa-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <span>Setor {{ generalSheet.sector.name }}</span>
          <t-btn
            icon="mdi-close"
            variant="text"
            aria-label="Fechar"
            @click="generalSheet = null"
          />
        </div>
        <v-alert
          v-if="describe(generalSheet.sector) !== null"
          class="sector-description text-caption mb-3"
          icon="mdi-information-outline"
          variant="tonal"
          color="warning"
          density="compact"
        >
          {{ describe(generalSheet.sector) }}
        </v-alert>
        <general-type-card
          v-for="type in generalSheet.section.tickets"
          :key="type.id"
          v-model="generalSheet.amounts[type.id]"
          :data-type="type.id"
          :sector="generalSheet.sector"
          :type="type"
          :event="event"
          :max="ticketCart.sheetMax(type, generalSheet)"
        />
        <div class="d-flex justify-end mt-3">
          <t-btn
            :block="mobile"
            color="success"
            @click="addGeneral"
          >
            Adicionar
          </t-btn>
        </div>
      </div>
    </v-bottom-sheet>

    <!-- A full-screen view has its own -->
    <cart-bar
      v-if="!fullScreen"
      :event="event"
      :groups="groups"
      :hold-left="holdLeft"
      :busy="advancing"
      @advance="advance"
      @remove="release"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch, type Ref } from "vue";
import { useDisplay } from "vuetify";
import { DateTime } from "luxon";
import formatCurrency from "@/filters/currency";
import type Event from "@/types/Event";
import type Row from "@/types/Mapping/Row";
import type Seat from "@/types/Mapping/Seat";
import type Sector from "@/types/Sector";
import type TicketType from "@/types/TicketType";
import { SeatingType } from "@/types/SeatingType";
import TBtn from "@/components/core/TBtn.vue";
import GeneralTypeCard from "@/components/Ticket/GeneralTypeCard.vue";
import SvgMap, { type SetupElement, type SvgMapExposed } from "@/components/Mapping/SvgMap.vue";
import CartBar from "@/components/checkout/CartBar.vue";
import SectionSeats from "@/components/checkout/SectionSeats.vue";
import SectorListing from "@/components/checkout/SectorListing.vue";
import getSectionRows from "@/api/requests/getSectionRows";
import { usePolling } from "@/composables/usePolling";
import { useTimer } from "@/composables/useTimer";
import { TrackableEvent } from "@/analytics/TrackableEvents";
import { useAnalytics } from "@/plugins/analytics";
import { useCaptcha } from "@/plugins/recaptcha";
import { useSettings } from "@/plugins/settings";
import { useCartStore } from "@/stores/cart";
import { useCheckoutStore } from "@/stores/checkout";
import { useDialog } from "@/stores/dialog";
import { useLoader } from "@/stores/loader";
import { useSnackbar } from "@/stores/snackbar";
import { useTicketCartStore, type Refusal } from "@/stores/ticketCart";
import { useUserStore } from "@/stores/user";
import Fee from "@/utils/fees";
import { seatLabel } from "@/utils/seatGrid";
import { panelForShape, sectorGroups, type SectionPanel, type SectorGroup } from "@/utils/sectionSelection";
import type { CartLine, GeneralPick } from "@/utils/ticketCart";

interface Props {
  event: Event;
}

const { event } = defineProps<Props>();

const emit = defineEmits<{
  next: [];
}>();

defineOptions({
  name: "MappedPurchaseStep",
});

/** How often the open Section's grid is reloaded, so seats others take meanwhile show as taken. */
const POLLING_INTERVAL = 7000;

/** A seat tapped on the grid, waiting for its type. */
interface Picking {
  panel: SectionPanel;
  seat: Seat;
}

const cart = useCartStore();
const ticketCart = useTicketCartStore();
const checkout = useCheckoutStore();
const userStore = useUserStore();
const dialog = useDialog();
const snackbar = useSnackbar();
const loader = useLoader();
const captcha = useCaptcha();
const analytics = useAnalytics();
const settings = useSettings();
const { mobile } = useDisplay();
const venueMap = useTemplateRef<SvgMapExposed>("venueMap");

/** The Section whose seat grid is shown. */
const opened: Ref<SectionPanel | null> = ref(null);

const rows: Ref<Row[]> = ref([]);

/** The open Section's seats are on their way. */
const loadingRows: Ref<boolean> = ref(false);

const picking: Ref<Picking | null> = ref(null);

/** The General sector whose sheet is open, with the amounts picked on it. */
const generalSheet: Ref<GeneralPick | null> = ref(null);

/** The page is validating and submitting the step, after Avançar. */
const advancing = computed<boolean>(() => loader.loading("button"));

/** On a phone, the event's map is open full screen. */
const mapOpen: Ref<boolean> = ref(false);

/** Fill of each map shape as drawn, by the id of the Sector or Section it is linked to. */
const colors: Ref<Record<string, string>> = ref({});

const mapUrl = computed<string | null>(() => event.configuration?.mapping?.image_url ?? null);

/** On a phone, the map or a Section fills the screen. */
const fullScreen = computed<boolean>(() => mobile.value && (mapOpen.value || opened.value !== null));

const groups = computed<SectorGroup[]>(() => sectorGroups(cart.sectors));

/** The type sheet tells each type's batch, as the ticket table of the unmapped flow does. */
const showBatch = computed<boolean>(() => !settings.match(event, "HIDE_BATCH_NUMBER"));

const polling = usePolling(reload, POLLING_INTERVAL);

/** Whether a reload is on its way, so ticks don't pile up on a slow API. */
let reloading = false;

/** Whether Avançar is reserving the Cart, which a reload mustn't change meanwhile. */
let submitting = false;

/** Whether the buyer moved on to another step, which keeps this one alive. */
let away = false;

/** Counts down to the seat hold's expiry, which every hold or release pushes back. */
const holdTimer = useTimer();

/** "14:59", the time left on the seat hold, null while no seat is held. */
const holdLeft = computed<string | null>(() => {
  if (ticketCart.expiresAt === null || !ticketCart.holdsSeats) return null;

  const { minutes, seconds } = holdTimer.display.value;

  return `${minutes}:${seconds}`;
});

watch(() => ticketCart.expiresAt, (expiresAt) => {
  if (expiresAt === null) {
    holdTimer.reset();
    return;
  }

  holdTimer.start({ expiresAt: DateTime.fromISO(expiresAt), onExpire: holdRanOut });
}, { immediate: true });

/**
 * The countdown reached zero: the API says which seats are still the buyer's, as a Reservation renews their holds. On
 * another step it's asked when the buyer comes back instead.
 */
function holdRanOut(): void {
  if (!away) void checkHolds(() => "O tempo para garantir seus lugares acabou, e eles saíram do seu carrinho.");
}

/**
 * Asks the API which of the Cart's seats the buyer still holds: the others leave the Cart, telling the buyer, and the
 * countdown takes the holds' expiry. When the API can't be asked, logged out or offline, the seats leave once their
 * hold has run out.
 */
async function checkHolds(tell: (dropped: CartLine[]) => string): Promise<void> {
  let dropped: CartLine[];

  try {
    dropped = userStore.logged ? await ticketCart.resume() : dropIfRanOut();
  } catch (error) {
    console.error("Error checking the held seats:", error);
    dropped = dropIfRanOut();
  }

  if (dropped.length > 0) snackbar.show(tell(dropped), "warning");
}

function dropIfRanOut(): CartLine[] {
  const expiresAt = ticketCart.expiresAt;

  return expiresAt !== null && DateTime.fromISO(expiresAt) <= DateTime.now() ? ticketCart.expire() : [];
}

// A Cart filled before logging in, or by someone else on this browser, is checked once the buyer logs in
watch(() => userStore.logged, (logged) => {
  if (logged) void checkHolds(lostMessage);
});

const seatsInCart = computed<Seat[]>(() => ticketCart.lines.flatMap(line => line.seat ?? []));

function fee(price: number): number {
  return Fee.calculate(price, event.convenience_fee, event.convenience_fee_mode);
}

/** What the event says about the Sector, such as what its tickets do and don't include. */
function describe(sector: Sector): string | null {
  return event.sector_configs?.find(config => config.sector_id === sector.id)?.description || null;
}

/** Keeps each shape's colour for the listing's rows, and greys out the shapes with nothing to buy. */
function paint(shapes: SetupElement[]): void {
  shapes.forEach(({ element, color }) => {
    colors.value[element.id] = color;

    if (panelForShape(groups.value, element.id) === null) venueMap.value?.disable(element.id);
  });
}

/** A shape opens what its row of the listing opens. */
function openShape(shapeId: string): void {
  const panel = panelForShape(groups.value, shapeId);
  if (panel === null) return;

  mapOpen.value = false;
  void open(panel);
}

/** A General (or Random) sector opens its sheet, a Selected-seat Section its seat grid. */
async function open(panel: SectionPanel): Promise<void> {
  if (panel.sector.seating_type !== SeatingType.SELECTED) {
    generalSheet.value = { sector: panel.sector, section: panel.section, amounts: {} };
    return;
  }

  opened.value = panel;
  rows.value = [];

  // Not the page's loader: the page swaps every step for a skeleton while it's up, which would remount this one and
  // lose the Section being opened
  loadingRows.value = true;

  try {
    const fresh = await getSectionRows(event.id, panel.section.id);

    // The buyer closed it, or opened another, meanwhile
    if (opened.value?.id !== panel.id) return;

    showGrid(fresh);
  } catch (error) {
    snackbar.error(error, "Não foi possível carregar os lugares.");
  } finally {
    if (opened.value?.id === panel.id) loadingRows.value = false;
  }

  if (opened.value?.id === panel.id && !away) polling.start();
}

/** Shows the Section's grid as just loaded, taking out of the Cart the seats it shows someone else took. */
function showGrid(fresh: Row[]): void {
  rows.value = fresh;

  const lost = ticketCart.dropTaken(fresh);

  if (lost.length > 0) snackbar.show(lostMessage(lost), "warning");
}

/** Tells which Cart seats someone else took, like "Os lugares A1 e A3 não estão mais disponíveis". */
function lostMessage(lost: CartLine[]): string {
  const labels = new Intl.ListFormat("pt-BR").format(lost.flatMap(line => line.seat?.full_label ?? []));

  return lost.length === 1
    ? `O lugar ${labels} não está mais disponível e saiu do seu carrinho.`
    : `Os lugares ${labels} não estão mais disponíveis e saíram do seu carrinho.`;
}

/** The X: back to the listing, to open another Section. */
function close(): void {
  opened.value = null;
  polling.stop();
}

/** Reloads the open Section's grid in the background. */
async function reload(): Promise<void> {
  const panel = opened.value;
  if (panel === null || reloading || submitting || away) return;

  reloading = true;

  try {
    const fresh = await getSectionRows(event.id, panel.section.id);

    // The buyer closed it, opened another, moved on or pressed Avançar meanwhile
    if (opened.value === panel && !away && !submitting) showGrid(fresh);
  } catch (error) {
    // The next tick tries again
    console.error("Error reloading the seat grid:", error);
  } finally {
    reloading = false;
  }
}

/** The step is kept alive when the buyer moves on or back, so the polling stops here, not on unmount. */
function leave(): void {
  away = true;
  polling.stop();

  // A phone's full-screen views sit over the whole page, so they'd stay in front of the next step
  mapOpen.value = false;
  if (mobile.value) opened.value = null;
}

function enter(): void {
  away = false;
  if (opened.value !== null) polling.start();
  void checkHolds(lostMessage);
}

onMounted(() => void checkHolds(lostMessage));

/** A seat in the Cart leaves it; any other free seat asks for its type, once the buyer is logged in. */
function tap(seat: Seat): void {
  const line = ticketCart.lines.find(l => l.seat?.id === seat.id);

  if (line !== undefined) {
    release(line);
    return;
  }

  const panel = opened.value;
  if (panel === null || ticketCart.pending.includes(seat.id)) return;

  if (userStore.logged) {
    picking.value = { panel, seat };
    return;
  }

  dialog.show("/login", {
    onClose: () => {
      if (userStore.logged) picking.value = { panel, seat };
    },
  });
}

/** Takes the ticket out of the Cart: a seat's hold is released, a General type's amount drops by one. */
function release(line: CartLine): void {
  ticketCart.remove(line)
    .then(() => track(TrackableEvent.RemoveFromCart, line.type, 1))
    .catch(error => snackbar.error(error, "Não foi possível liberar o lugar."));
}

/** Tells analytics about tickets the buyer put in or took out of the Cart, at the price the Cart charges. */
function track(trackable: TrackableEvent.AddToCart | TrackableEvent.RemoveFromCart, type: TicketType, quantity: number): void {
  analytics.collect(trackable, {
    producer_id: event.cliente_id,
    params: {
      currency: "BRL",
      items: { item_name: type.name, item_id: type.id, price: type.current_batch?.price ?? 0, quantity },
    },
  });
}

async function pick(type: TicketType): Promise<void> {
  if (picking.value === null) return;

  const { panel, seat } = picking.value;
  picking.value = null;

  let result;

  try {
    result = await ticketCart.addSeat(panel.sector, panel.section, seat, type);
  } catch (error) {
    snackbar.error(error, "Não foi possível reservar o lugar.");
    return;
  }

  if (result === "added") {
    track(TrackableEvent.AddToCart, type, 1);
  } else if (result === "taken") {
    seat.status = "reserved";
    snackbar.show(`O lugar ${seat.full_label} acabou de ser reservado por outra pessoa.`, "warning");
  } else {
    refuse(result, type);
  }
}

function refuse(refusal: Refusal, type: TicketType): void {
  if (refusal === "full") {
    snackbar.show(`Você pode levar até ${event.quantidade_ingresso_venda_online} ingressos.`, "warning");
  } else {
    snackbar.show(`Você atingiu o limite de ingressos ${type.name} por pessoa.`, "warning");
  }
}

/**
 * Adds the amounts picked on the General sheet to the Cart. A logged-out buyer logs in first, and gets the sheet back
 * as they left it. When the Cart refuses a type, the sheet stays open with that type's amount and the ones after it, as
 * it does when nothing was picked.
 */
function addGeneral(): void {
  const sheet = generalSheet.value;
  if (sheet === null) return;

  if (Object.values(sheet.amounts).every(amount => !amount)) {
    snackbar.show("Escolha ao menos um ingresso.", "error");
    return;
  }

  generalSheet.value = null;

  if (!userStore.logged) {
    dialog.show("/login", {
      onClose: () => {
        if (userStore.logged) generalSheet.value = sheet;
      },
    });
    return;
  }

  for (const type of sheet.section.tickets) {
    const amount = sheet.amounts[type.id] ?? 0;
    if (amount === 0) continue;

    const result = ticketCart.addGeneral(sheet.sector, sheet.section, type, amount);

    if (result !== "added") {
      refuse(result, type);
      generalSheet.value = sheet;
      return;
    }

    track(TrackableEvent.AddToCart, type, amount);

    sheet.amounts[type.id] = 0;
  }
}

/** Avançar: once, while the page reserves the Cart. */
function advance(): void {
  if (!advancing.value) emit("next");
}

function validate(): boolean {
  if (ticketCart.count === 0) throw new Error("Escolha ao menos um ingresso.");

  return true;
}

async function submit(): Promise<boolean> {
  submitting = true;

  try {
    const token = await captcha.submit("submit");

    await checkout.reserveCart(token);
  } finally {
    submitting = false;
  }

  return true;
}

defineExpose({
  enter,
  leave,
  validate,
  submit,
});
</script>

<style scoped>
.side {
  position: sticky;
  top: 80px;
}

.full-screen {
  min-height: 100dvh;
}

.type-card {
  background: white;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  text-align: left;
}

.type-card:disabled {
  opacity: 0.5;
}
</style>
